import { clearScoresHistogram, fetchScoreHistogram } from 'actions';
import SelectRedesign from 'components/Select/SelectRedesign';
import ExploreFiltersTreeContext from 'contexts/ExploreFiltersTreeContext';
import { round } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getRulePrompts } from 'selectors/ruleGroups';
import { useSelector } from 'store';
import RiskHistogram from './RiskHistogram';
import RiskScoreSlider from './RiskScoreSlider';

const ScoreFilter: React.FC = () => {
  const dispatch = useDispatch();
  const { treeParams, filtersSearchString, handleReplaceValueInTree, handleRemoveFieldFromTree } =
    useContext(ExploreFiltersTreeContext);

  const prompts = useSelector(getRulePrompts);

  const [showMore, setShowMore] = useState(true);
  const [threshold, setThreshold] = useState([0, 1]);
  const [stringThreshold, setStringThreshold] = useState(['0', '1']);

  const parsePrompt = (treeParams.prompt as string[])?.reduce<Record<string, string>>(
    (acc, val) => {
      const [key, value] = val.split(':');

      if (key && value) {
        return { ...acc, [key]: value };
      }
      return acc;
    },
    {}
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const treeScore = useMemo(() => parsePrompt?.score?.split('<>') || [], [filtersSearchString]);
  const promptId = parsePrompt?.uuid || '';
  const facet = parsePrompt?.facet || '';

  const promptOptions = Object.entries(prompts)?.reduce<{ value: string; label: string }[]>(
    (acc, [key, value]) => {
      const facetOptions = value.facets?.map((val) => ({
        value: `${key}<>${val}`,
        label: `${value.name} - ${val}`,
      }));
      return [...acc, ...facetOptions];
    },
    []
  );

  useEffect(
    () => () => {
      dispatch(clearScoresHistogram());
      handleRemoveFieldFromTree('prompt');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!treeParams.rule_uuids || treeParams.rule_uuids.length !== 1) {
      handleRemoveFieldFromTree('prompt');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, treeParams.rule_uuids]);

  useEffect(() => {
    if (promptId && facet) {
      dispatch(fetchScoreHistogram({ filtersSearch: filtersSearchString }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, promptId, facet]);

  useEffect(() => {
    if (treeScore && treeScore.length === 2) {
      const lowValue = parseFloat(treeScore[0]);
      const highValue = parseFloat(treeScore[1]);

      setThreshold([lowValue, highValue]);
      setStringThreshold([treeScore[0], treeScore[1]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeScore]);

  const handleShowMore = (): void => {
    setShowMore(!showMore);
  };

  const handleChangeStringThreshold = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name } = e.target;
    const numValue = Number.parseFloat(e.target.value.slice(0, 4));
    const { value } = e.target;

    const lowValueWithSeparation = round(threshold[0] + 0.1, 2);
    const highValueWithSeparation = round(threshold[1] - 0.1, 2);

    if (value) {
      if (name === 'low') {
        if (numValue >= 0 && numValue <= highValueWithSeparation) {
          setThreshold((t) => [numValue, t[1]]);
        }
        setStringThreshold((t) => [value, t[1]]);
      } else {
        if (numValue <= 1 && numValue >= lowValueWithSeparation) {
          setThreshold((t) => [t[0], numValue]);
        }
        setStringThreshold((t) => [t[0], value]);
      }
    } else if (name === 'low') {
      setStringThreshold((t) => [value, t[1]]);
    } else {
      setStringThreshold((t) => [t[0], value]);
    }
  };

  const handleChangeThreshold = (value: number[]): void => {
    setThreshold(value);
    setStringThreshold([value[0].toString(), value[1].toString()]);
  };

  const handleSubmit = (value: number[]): void => {
    handleReplaceValueInTree(
      `uuid:${promptId} & facet:"${facet}" & score:${value[0].toString()}<>${value[1].toString()}`,
      'prompt',
      '',
      true
    );
  };

  const handleEnterRangeSearch: React.KeyboardEventHandler<HTMLInputElement> = (e): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(threshold);
    }
  };

  const handleSelectPrompt = (value: string): void => {
    const [promptValue, facetValue] = value.split('<>');

    handleReplaceValueInTree(
      `uuid:${promptValue} & facet:"${facetValue}" & score:${stringThreshold[0].toString()}<>${stringThreshold[1].toString()}`,
      'prompt',
      '',
      true
    );
  };

  return (
    <div className="pt-2 w-full flex flex-col gap-1 text-body">
      {showMore && (
        <>
          <div>
            <div className="px-2 flex flex-col gap-1">
              <span className="text-white">Prompt</span>
              <div className="bg-litlingo-success-light bg-opacity-10 rounded ">
                <SelectRedesign
                  onChange={handleSelectPrompt}
                  options={promptOptions}
                  value={`${promptId}<>${facet}`}
                  className="text-white bg-transparent border-none"
                  valueClassName="text-white"
                  optionClassname="text-litlingo-gray-6"
                />
              </div>
            </div>
            <div className="relative h-30 mt-2 mx-2.5 bg-litlingo-primary-120">
              <div className="h-full w-full bg-litlingo-primary-140">
                <RiskHistogram />
              </div>
              <div
                className="absolute h-full bg-litlingo-gray-2 bg-opacity-20 bottom-0"
                style={{ left: 198 * threshold[0], right: 198 - 198 * threshold[1] }}
              />
            </div>
            <RiskScoreSlider
              threshold={threshold}
              handleChangeThreshold={handleChangeThreshold}
              handleSubmit={handleSubmit}
              disabled={!promptId || !facet}
            />
          </div>

          <div className="mt-7.5 flex flex-row items-center gap-2 text-white">
            <span className="italic">Range</span>
            <input
              id="low"
              name="low"
              type="number"
              min={0}
              max={stringThreshold[1]}
              step={0.01}
              maxLength={3}
              className="h-8 w-12.5 py-1.5 px-1 bg-litlingo-gray-2 bg-opacity-20 rounded"
              data-testid="name-input"
              value={stringThreshold[0]}
              onChange={handleChangeStringThreshold}
              onKeyDown={handleEnterRangeSearch}
              onBlur={(): void => {
                handleSubmit(threshold);
              }}
              disabled={!promptId || !facet}
            />
            <span className="italic">to</span>
            <input
              id="high"
              name="high"
              type="number"
              min={stringThreshold[0]}
              max={1}
              step={0.01}
              maxLength={3}
              className="h-8 w-12.5 py-1.5 px-1 bg-litlingo-gray-2 bg-opacity-20 rounded"
              data-testid="name-input"
              value={stringThreshold[1]}
              onChange={handleChangeStringThreshold}
              onKeyDown={handleEnterRangeSearch}
              onBlur={(): void => {
                handleSubmit(threshold);
              }}
              disabled={!promptId || !facet}
            />
          </div>
        </>
      )}
      <button
        type="button"
        className="mt-2 self-end underline text-white focus:outline-none"
        onClick={handleShowMore}
      >
        {showMore ? 'Show Less' : 'Show More'}
      </button>
    </div>
  );
};

export default ScoreFilter;
